import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Icons } from '../../../components/base/BootstrapComponents';
import { optionClassFormatter } from '../../../utils/formatters';
import Dropdown from '../../../components/base/Dropdown/Dropdown';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import Tooltip from '../../../components/base/Tooltip/Tooltip';
import {
  getHostMetricsByKeys,
  responseLogWithNotification,
} from '../../../utils/utils';
import notificationDecorator from '../../Notifications/notificationDecorator';
import {
  hostCommandDispatch, loadHostComponents, showHistoryPopup, loadHostProblems,
} from '../../ParkingMeter/actions';
import {
  getCameraStatus, getVersionStatus,
} from '../camera-utils';
import { getMetricValue } from '../../ParkingMeter/parking-meter-utils';
import { getCamerasData } from '../reducer';
import { intlShape } from '../../../components/base/IntlProviderWrapper';
import {
  APP_STATE,
  cMetricKeys,
} from '../constants';

import { PARKING_METER_COMMAND } from '../../ParkingMeter/constants';
import PermissionAccessWrapper from '../../../components/base/PermissionAccessWrapper';

export function ProblemTable({ problems }) {
  const [scroll, setScroll] = useState(null);
  useEffect(() => {
    const a = new window.IScroll('#problems-scroll-wrapper', {
      scrollbars: true,
      mouseWheel: true,
      preventDefault: false,
      interactiveScrollbars: true,
    });
    setScroll(a);
  }, []);
  return (
    <div className="scroll-wrapper problems-scroll-wrapper" id="problems-scroll-wrapper">
      <div className="scroll">
        <table className="wsh-info-table">
          <tbody>
            {problems && problems.length ? problems.map((item, key) => (
              <tr className="wsh-info-table-row" key={key}>
                <td className="wsh-info-table-cell wsh-date">{item.time ? moment(item.time).format('DD.MM.YY HH:mm') : ''}</td>
                <td className={classNames('wsh-info-table-cell wsh-message', optionClassFormatter(item.status))}>{item.message}</td>
              </tr>
            ))
              : <tr><td colSpan={2}>Проблемы не найдены</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export function CamerasActionsFormatter({ item, isMobile }) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const showHistory = () => {
    dispatch(showHistoryPopup(item.getData()));
  };
  const handleHostCommandDispatch = command => () => {
    dispatch(hostCommandDispatch({ id: item.id, command })).then(() => {
      notificationDecorator({
        title: 'Создание команды',
        message: formatMessage({ id: 'success' }),
      });
    }).catch(response => {
      responseLogWithNotification(response, 'Создание команды');
    });
  };
  const handleOpenZabbix = () => {
    window.open(item.external_link, '_blank');
  };

  return (
    <div className="actions-formatter">
      <div className="actions-formatter-buttons">
        <PermissionAccessWrapper requiredPermissions={['execute_device_reboot_command']} locked>
          <Button className={classNames('action-button replay-action', { isMobile })} onClick={handleHostCommandDispatch(PARKING_METER_COMMAND.DEVICE_REBOOT)}>
            <Icons customIcon icon="replay" />
          </Button>
        </PermissionAccessWrapper>
        <PermissionAccessWrapper requiredPermissions={['execute_software_reboot_command']} locked>
          <Button className="action-button unlock-top-door-action" onClick={handleHostCommandDispatch(PARKING_METER_COMMAND.SOFTWARE_REBOOT)}>
            <Icons customIcon icon="loop" />
          </Button>
        </PermissionAccessWrapper>
        <Button className="action-button unlock-bottom-door-action" onClick={handleOpenZabbix}>
          <Icons customIcon icon={isMobile ? 'zabbixCameras' : 'zabbix'} />
        </Button>
        {isMobile
          ? (
            <Button className="action-button replay-action" onClick={showHistory}>
              <Icons customIcon icon="listCameras" />
            </Button>
          ) : null}
      </div>
      <div className="actions-formatter-history f5" onClick={showHistory}>
        История
      </div>
    </div>
  );
}
export function CamerasWorkingStateFormatter({ item, isPopup }) {
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(false);
  const [components, setComponents] = useState(null);
  const anchorElement = useRef(null);
  const onLoadHostComponents = () => {
    dispatch(loadHostComponents(item.id)).then(comps => { setComponents(comps); setOpen(true); }).catch(responseLogWithNotification);
  };
  const metrics = getHostMetricsByKeys(item, []);
  const { status: versionStatus, statusText: versionStatusText } = getVersionStatus(metrics);
  const { status: cameraStatus, statusText: cameraStatusText } = getCameraStatus(metrics);
  return (
    <div className="working-state-formatter">
      <div className="working-state-wrapper">
        <div className="working-state-block">
          <div className="working-state-name">Версия:</div>
          <div className={classNames('working-state-value', optionClassFormatter(versionStatus))}>{versionStatusText}</div>
        </div>
        <div className="working-state-block">
          <div className="working-state-name">Статус:</div>
          {
            isPopup
              ? (
                <Tooltip
                  content={<div className={classNames('working-state-value', optionClassFormatter(cameraStatus), { popup: isPopup })}>{cameraStatusText}</div>}
                  tooltipContent={<div className={classNames('working-state-value', optionClassFormatter(cameraStatus))}>{cameraStatusText}</div>}
                  className={classNames('cam-status-number-tooltip', { popup: isPopup })}
                />
              )
              : <div className={classNames('working-state-value', optionClassFormatter(cameraStatus))}>{cameraStatusText}</div>
          }
        </div>
      </div>
      {item.host_component_template_id
        ? (
          <div className="working-state-helper">
            <Dropdown
              usePortal
              customControl
              customOpen={isOpen}
              customHandleClose={() => { setOpen(false); }}
            >
              <Dropdown.Toggle>
                <div
                  onClick={onLoadHostComponents}
                  className={classNames('tooltip-content')}
                  ref={anchorElement}
                >
                  <Icons customIcon icon="info" className="wsh-info" />
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu
                className={classNames('portal-tooltip-wrapper wsh-portal-tooltip-wrapper')}
                anchorElement={anchorElement}
                classNamePortal="portal-tooltip wsh-portal"
                useResizeContent={false}
              >
                <div
                  className="portal-tooltip-popup"
                >
                  <div className="wsh-info-tooltip">
                    <div className="wsh-info-title">
                      Компонентная база
                      <Icons icon="close" customIcon className="close" onClick={() => { setOpen(false); }} />
                    </div>
                    <table className="wsh-info-table">
                      {components && components.values && components.values.length && components.values.map((item, key) => (
                        <tr className="wsh-info-table-row" key={key}>
                          <td className="wsh-info-table-cell wsh-key">{item.key}</td>
                          <td className="wsh-info-table-cell wsh-value">{item.value}</td>
                        </tr>
                      ))}
                    </table>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )
        : null}
    </div>
  );
}
export function StatusFormatter({ item }) {
  const { serverTimezone } = useSelector(getCamerasData);
  const {
    formatMessage,
  } = intlShape;
  if (!item) {
    return null;
  }
  const { status, last_ping_at } = item;
  const currentDay = moment().day();
  const lastPingAt = moment(last_ping_at);
  const lastPingAtDay = moment(last_ping_at).day();
  const metrics = getHostMetricsByKeys(item, cMetricKeys);
  const state = Number(getMetricValue(metrics)('APP_STATE'));

  const service_mode = state === APP_STATE.SERVICE;
  return (
    <div className={classNames('status-formatter', optionClassFormatter(status))}>
      <div className="status-text">{formatMessage({ id: `parking.meter.status.${status}`, defaultMessage: formatMessage({ id: 'unknown' }) })}</div>
      <div className="last-ping-at">
        {serverTimezone && last_ping_at ? <span className="pmh-time-wrapper">{lastPingAt.tz(serverTimezone).format(lastPingAtDay === currentDay ? 'HH:mm' : 'DD.MM.YY HH:mm')}</span> : null}
        {service_mode ? <span className="pmh-icon-wrapper"><Icons customIcon icon="construction" className="pmh-icon construction" /></span> : null}
      </div>
    </div>
  );
}
export function StatusFormatterWithProblem({ item }) {
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(false);
  const anchorElement = useRef(null);
  const [problems, setProblems] = useState(null);
  const onLoadHostProblems = () => {
    dispatch(loadHostProblems(item.id)).then(problems => { setProblems(problems); setOpen(true); }).catch(responseLogWithNotification);
  };
  return (
    <Dropdown
      usePortal
      customControl
      customOpen={isOpen}
      useResizeContent={false}
      customHandleClose={() => { setOpen(false); }}
    >
      <Dropdown.Toggle className="status-with-problems-toggle">
        <div
          onClick={onLoadHostProblems}
          className={classNames('status-with-problems')}
          ref={anchorElement}
        >
          <StatusFormatter item={item} />
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu
        className={classNames('portal-tooltip-wrapper wsh-portal-tooltip-wrapper')}
        anchorElement={anchorElement}
        classNamePortal="portal-tooltip wsh-portal"
        useResizeContent={false}
      >
        <div
          className="portal-tooltip-popup"
        >
          <div className="wsh-info-tooltip host-problems">
            <div className="wsh-info-title">
              Проблемы
              <Icons icon="close" customIcon className="close" onClick={() => { setOpen(false); }} />
            </div>
            {problems ? <ProblemTable problems={problems} /> : null}
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
export function MoreFormatter({ item }) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const handleOpenZabbix = () => {
    window.open(item.external_link, '_blank');
  };
  const handleHostCommandDispatch = command => () => {
    dispatch(hostCommandDispatch({ id: item.id, command })).then(() => {
      notificationDecorator({
        title: 'Создание команды',
        message: formatMessage({ id: 'success' }),
      });
    }).catch(response => {
      responseLogWithNotification(response, 'Создание команды');
    });
  };
  return (
    <TooltipWithPortal
      showByClick
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="mpf-more-actions"
      tooltipContent={(
        <div className="parking-meter-more-wrapper">
          <PermissionAccessWrapper requiredPermissions={['execute_software_reboot_command']} locked>
            <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleHostCommandDispatch(PARKING_METER_COMMAND.SOFTWARE_REBOOT)}>
              <div className="parking-meter-more-button-icon">
                <Icons width={16} height={22} customIcon icon="loop" className="button-icons" />
              </div>
              Перезапуск ПО
            </Button>
          </PermissionAccessWrapper>
          <PermissionAccessWrapper requiredPermissions={['execute_payment_reboot_command']} locked>
            <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleHostCommandDispatch(PARKING_METER_COMMAND.PAYMENT_REBOOT)}>
              <div className="parking-meter-more-button-icon">
                <Icons width={24} height={24} customIcon icon="credit-card" className="button-icons" />
              </div>
              Перезагрузка платежного контроллера
            </Button>
          </PermissionAccessWrapper>
          <PermissionAccessWrapper requiredPermissions={['execute_service_mode_command']} locked>
            <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleHostCommandDispatch(PARKING_METER_COMMAND.SERVICE_MODE)}>
              <div className="parking-meter-more-button-icon">
                <Icons width={18} height={14} customIcon icon="reorder" className="button-icons" />
              </div>
              Сервисное меню
            </Button>
          </PermissionAccessWrapper>
          <PermissionAccessWrapper requiredPermissions={['execute_test_command']} locked>
            <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleHostCommandDispatch(PARKING_METER_COMMAND.TEST)}>
              <div className="parking-meter-more-button-icon">
                <Icons width={18} height={18} customIcon icon="ballot" className="button-icons" />
              </div>
              Комплексный тест
            </Button>
          </PermissionAccessWrapper>
          <PermissionAccessWrapper requiredPermissions={['execute_hibernate_command']} locked>
            <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleHostCommandDispatch(PARKING_METER_COMMAND.HYBERNATE)}>
              <div className="parking-meter-more-button-icon">
                <Icons width={15} height={20} customIcon icon="brightness-2" className="button-icons" />
              </div>
              Перейти в режим сна
            </Button>
          </PermissionAccessWrapper>
          <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleOpenZabbix}>
            <div className="parking-meter-more-button-icon">
              <Icons width={24} height={24} customIcon icon="zabbix" className="button-icons" />
            </div>
            Перейти в ZABBIX
          </Button>
        </div>
      )}
      classNameWrapper="parking-meter-more-tooltip"
      className="parking-meter-more"
    />
  );
}
